import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Phone,
  Check,
  Hammer,
  Paintbrush,
  ShieldCheck,
  Building2,
  Wind,
  Zap,
  DoorOpen,
  Wrench,
  ArrowRight,
} from "lucide-react";
import { Footer } from "../components/site/Footer";
import { Seo } from "../components/site/Seo";
import { Breadcrumbs } from "../components/site/Breadcrumbs";
import { getServiceBySlug } from "../data/services";
import { SITE } from "../lib/site";

const iconMap = { Hammer, Paintbrush, ShieldCheck, Building2, Wind, Zap, DoorOpen, Wrench };

function ServiceCtas({ service, dark = false, testPrefix }) {
  const phoneDigits = SITE.phoneTel.replace(/\D/g, "");
  const whatsappHref = service.whatsappMessage
    ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(service.whatsappMessage)}`
    : SITE.whatsapp;
  const secondaryClass = dark
    ? "border-white/30 text-white hover:bg-white hover:text-zinc-950"
    : "border-zinc-950 text-zinc-950 hover:bg-zinc-950 hover:text-white";

  if (service.emergency) {
    return (
      <>
        <a
          href={`tel:${SITE.phoneTel}`}
          data-testid={`${testPrefix}-call`}
          className="group inline-flex items-center justify-between gap-6 px-6 py-4 bg-[#00844A] text-white hover:bg-[#006F3E] transition-all duration-300"
        >
          <span className="font-bold text-sm tracking-wide">
            {service.ctaLabel} · {SITE.phoneDisplay}
          </span>
          <Phone size={16} />
        </a>
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testPrefix}-whatsapp`}
          className={`group inline-flex items-center justify-between gap-6 px-6 py-4 border transition-all duration-300 ${secondaryClass}`}
        >
          <span className="font-bold text-sm tracking-wide">
            {service.whatsappSecondaryLabel || "WhatsApp’tan Yaz"}
          </span>
          <ArrowUpRight
            size={18}
            className="group-hover:rotate-45 transition-transform duration-300"
          />
        </a>
      </>
    );
  }

  return (
    <>
      <a
        href={whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testPrefix}-whatsapp`}
        className="group inline-flex items-center justify-between gap-6 px-6 py-4 bg-[#00844A] text-white hover:bg-[#006F3E] transition-all duration-300"
      >
        <span className="font-bold text-sm tracking-wide">
          {service.ctaLabel || "WhatsApp’tan Hızlı Fiyat Sor"}
        </span>
        <ArrowUpRight
          size={18}
          className="group-hover:rotate-45 transition-transform duration-300"
        />
      </a>
      <a
        href={`tel:${SITE.phoneTel}`}
        data-testid={`${testPrefix}-call`}
        aria-label={`${service.callLabel || "Telefonla Ara"}: ${SITE.phoneDisplay}`}
        className={`group inline-flex items-center justify-between gap-6 px-6 py-4 border transition-all duration-300 ${secondaryClass}`}
      >
        {service.callLabel ? (
          <span
            aria-hidden="true"
            className="grid font-bold text-sm tracking-wide"
          >
            <span className="col-start-1 row-start-1 transition-all duration-200 group-hover:-translate-y-1 group-hover:opacity-0 group-focus:-translate-y-1 group-focus:opacity-0">
              {service.callLabel}
            </span>
            <span className="col-start-1 row-start-1 translate-y-1 opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100 group-focus:translate-y-0 group-focus:opacity-100">
              {SITE.phoneDisplay}
            </span>
          </span>
        ) : (
          <span className="font-bold text-sm tracking-wide">{SITE.phoneDisplay}</span>
        )}
        <Phone size={16} />
      </a>
    </>
  );
}

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!service) return <Navigate to="/" replace />;

  const Icon = iconMap[service.icon] || Wrench;
  const related = service.related.map(getServiceBySlug).filter(Boolean);

  const canonical = `https://${SITE.domain}/hizmetler/${service.slug}`;

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.metaDescription,
    provider: {
      "@type": "LocalBusiness",
      name: "Artı Bir Yapı",
      telephone: SITE.phoneTel,
      address: {
        "@type": "PostalAddress",
        streetAddress: "Şerifali Mahallesi, İbrahim Hakkı Sokak No: 21/1",
        addressLocality: "Ümraniye",
        addressRegion: "İstanbul",
        addressCountry: "TR",
      },
      url: `https://${SITE.domain}`,
    },
    areaServed: { "@type": "City", name: "İstanbul" },
    serviceType: service.name,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: service.name,
      itemListElement: service.items.map((item) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: item },
      })),
    },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: service.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <Seo
        title={service.metaTitle}
        description={service.metaDescription}
        keywords={service.keywords}
        canonical={canonical}
        image={service.heroImage}
        jsonLd={[serviceJsonLd, faqJsonLd]}
      />

      <Breadcrumbs
        items={[
          { label: "Hizmetler", href: "/#hizmetler" },
          { label: service.name },
        ]}
      />

      {/* Hero */}
      <section
        data-testid="service-hero"
        className="max-w-7xl mx-auto px-6 sm:px-8 pb-16"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-zinc-500 mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-zinc-950" />
              {service.emergency
                ? "7/24 Acil Hizmet"
                : service.featured
                  ? "Öne Çıkan Hizmet"
                  : `Hizmet ${service.number}`}
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-display font-black text-zinc-950 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tighter leading-[0.95]"
              data-testid="service-h1"
            >
              {service.name}
            </motion.h1>
            <p className="mt-6 text-lg text-zinc-500 max-w-2xl italic font-medium">
              {service.tagline}
            </p>
            <p className="mt-8 text-base sm:text-lg text-zinc-700 max-w-2xl leading-relaxed">
              {service.intro}
            </p>

            {service.offer && (
              <div
                className="mt-8 max-w-2xl border border-zinc-200 border-t-4 border-t-[#00844A] bg-zinc-50"
                data-testid="service-starting-offer"
              >
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 p-5 sm:p-6">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#00844A]">
                      {service.offer.label}
                    </div>
                    <div className="mt-2 font-display font-bold text-lg text-zinc-950 tracking-tight">
                      {service.offer.audience}
                    </div>
                  </div>
                  <div className="sm:text-right shrink-0">
                    <div className="font-display font-black text-3xl sm:text-4xl text-zinc-950 tracking-tighter">
                      {service.offer.price}
                    </div>
                    <div className="text-xs font-semibold text-zinc-500 mt-1">
                      {service.offer.priceSuffix}
                    </div>
                  </div>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 border-t border-zinc-200 bg-white p-5 sm:p-6">
                  {service.offer.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5 text-sm font-medium text-zinc-800">
                      <Check size={16} className="mt-0.5 shrink-0 text-[#00844A]" strokeWidth={2.5} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <p className="border-t border-zinc-200 px-5 py-4 sm:px-6 text-[11px] leading-relaxed text-zinc-500">
                  {service.offer.note}
                </p>
              </div>
            )}

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <ServiceCtas service={service} testPrefix="service-cta" />
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-zinc-100">
              <img
                src={service.heroImage}
                alt={service.name}
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/40 via-transparent to-transparent" />
              {service.heroLogoOverlay ? (
                <div className="absolute bottom-1 sm:bottom-2 left-0 right-0 flex justify-center px-5">
                  <img
                    src="/assets/artibir-logo.png"
                    alt="Artı Bir Yapı"
                    className="h-10 sm:h-12 w-auto max-w-[290px] object-contain"
                  />
                </div>
              ) : (
                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white">
                  <Icon size={36} strokeWidth={1.5} className="mb-4 opacity-90" />
                  <div className="font-display font-bold text-xl sm:text-2xl tracking-tight">
                    {service.shortName}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Scope */}
      <section
        data-testid="service-scope"
        className="bg-zinc-50 py-20 sm:py-24 border-y border-zinc-200"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <div className="text-xs tracking-[0.25em] uppercase font-bold text-zinc-500 mb-5 flex items-center gap-3">
                <span className="w-8 h-px bg-zinc-950" />
                Hizmet Kapsamımız
              </div>
              <h2 className="font-display font-black text-zinc-950 text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.98]">
                {service.name} <br />
                <span className="italic font-medium text-zinc-400">
                  altında sunduğumuz tüm uygulamalar.
                </span>
              </h2>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {service.items.map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i * 0.03 }}
                    className="flex items-start gap-3 py-3 border-b border-zinc-200"
                    data-testid={`service-item-${i}`}
                  >
                    <Check size={18} className="mt-0.5 text-[#00844A] shrink-0" strokeWidth={2.5} />
                    <span className="text-zinc-800 font-medium">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section data-testid="service-benefits" className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-zinc-500 mb-5 flex items-center gap-3">
            <span className="w-8 h-px bg-zinc-950" />
            Neden Artı Bir Yapı
          </div>
          <h2 className="font-display font-black text-zinc-950 text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.98] mb-12 max-w-3xl">
            Öngörülebilir süreç,{" "}
            <span className="italic font-medium text-zinc-400">
              teslim edilebilir sonuç.
            </span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
            {service.benefits.map((b, i) => (
              <div
                key={i}
                data-testid={`service-benefit-${i}`}
                className="bg-white p-8 hover:bg-zinc-950 group transition-colors duration-500"
              >
                <div className="text-xs font-mono text-zinc-400 mb-10 group-hover:text-[#00844A]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-display font-bold text-xl text-zinc-950 group-hover:text-white tracking-tight mb-3 transition-colors">
                  {b.title}
                </div>
                <div className="text-sm text-zinc-600 group-hover:text-zinc-300 transition-colors leading-relaxed">
                  {b.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        data-testid="service-faq"
        className="py-20 sm:py-24 bg-zinc-50 border-y border-zinc-200"
      >
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-zinc-500 mb-5 flex items-center gap-3">
            <span className="w-8 h-px bg-zinc-950" />
            Sıkça Sorulan Sorular
          </div>
          <h2 className="font-display font-black text-zinc-950 text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.98] mb-12">
            {service.shortName.toLocaleLowerCase("tr-TR")} hakkında{" "}
            <span className="italic font-medium text-zinc-400">
              merak edilenler.
            </span>
          </h2>
          <div className="space-y-3">
            {service.faq.map((f, i) => (
              <details
                key={i}
                data-testid={`service-faq-${i}`}
                className="group bg-white border border-zinc-200 open:border-zinc-950 transition-colors"
              >
                <summary className="flex items-start justify-between gap-4 p-6 cursor-pointer list-none">
                  <span className="font-display font-bold text-base sm:text-lg text-zinc-950 tracking-tight">
                    {f.q}
                  </span>
                  <span className="shrink-0 mt-1 w-6 h-6 flex items-center justify-center bg-zinc-950 text-white text-lg font-bold group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <div className="px-6 pb-6 text-zinc-600 leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="service-cta-section"
        className="bg-zinc-950 text-white py-20 sm:py-24"
      >
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            {service.ctaHeading || `${service.shortName} için`} <br />
            <span className="italic font-medium text-zinc-500">
              {service.ctaSubheading || "ücretsiz keşif isteyin."}
            </span>
          </h2>
          <p className="mt-6 text-zinc-400 max-w-xl mx-auto leading-relaxed">
            {service.ctaDescription ||
              "İstanbul geneli için aynı gün dönüş yapıyoruz. Telefon veya WhatsApp ile hemen ulaşabilirsiniz."}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <ServiceCtas service={service} dark testPrefix="service-bottom-cta" />
          </div>
        </div>
      </section>

      {/* Related */}
      <section data-testid="service-related" className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs tracking-[0.25em] uppercase font-bold text-zinc-500 mb-4 flex items-center gap-3">
                <span className="w-8 h-px bg-zinc-950" />
                Diğer Hizmetlerimiz
              </div>
              <h2 className="font-display font-black text-zinc-950 text-3xl sm:text-4xl tracking-tighter">
                İlgili çözümler.
              </h2>
            </div>
            <Link
              to="/#hizmetler"
              className="text-sm font-bold text-zinc-950 hover:text-[#00844A] flex items-center gap-2"
            >
              Tüm Hizmetler <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {related.map((r, i) => {
              const RelatedIcon = iconMap[r.icon] || Wrench;
              return (
                <Link
                  key={r.slug}
                  to={`/hizmetler/${r.slug}`}
                  data-testid={`related-service-${i}`}
                  className="group bg-white p-8 hover:bg-[#00844A] transition-colors duration-500"
                >
                  <div className="flex items-center justify-between mb-10">
                    <div className="w-12 h-12 flex items-center justify-center bg-zinc-100 group-hover:bg-white transition-colors">
                      <RelatedIcon size={22} strokeWidth={1.75} className="text-zinc-950" />
                    </div>
                    <div className="text-xs font-mono text-zinc-400">
                      {r.number}
                    </div>
                  </div>
                  <h3 className="font-display font-bold text-xl text-zinc-950 group-hover:text-white tracking-tight mb-3 transition-colors">
                    {r.name}
                  </h3>
                  <p className="text-sm text-zinc-600 group-hover:text-zinc-300 leading-relaxed transition-colors">
                    {r.tagline}
                  </p>
                  <div className="mt-6 flex items-center gap-2 text-xs font-bold text-[#00844A] group-hover:text-white">
                    Detayları Gör <ArrowRight size={13} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
